import React, { useCallback, useEffect, useRef, useState } from 'react';
import classes from './People360.module.scss';
import { BannerDescription } from 'new/elements/BannerDescription';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import { useDispatch } from 'react-redux';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_PEOPLE_360 } from 'new/constants/pathnames';
import { fetchOrgMembers, loadMyTeams } from 'js/actions';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { ControlProps, SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';
import { PillTabs } from 'new/elements/PillTabs';
import { User } from 'new/entities/User';
import { PillTab } from 'new/elements/PillTabs/PillTabs';
import { AclEntitySelect } from 'new/components/collaborate/AclEntitySelect';
import { Button } from 'new/elements/Button';
import {
  RESOURCE_TYPE_CREDENTIALS,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_RECORD_TRANSFORM,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import useSharedResourcesInfo from 'new/hooks/adapters/useSharedResourcesInfo';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { Table } from 'new/elements/Table';
import columns from 'new/components/collaborate/People360/columns';

interface People360Props {}

const description =
  'Enhance transparency and control by clearly seeing the resource access you’ve granted to others, as well as the resource access granted to you by others.';

const controls = (user: User) =>
  [
    {
      value: 'me',
      label: 'Shared with Me',
    },
    {
      value: 'user',
      label: `Shared with ${user?.full_name?.split(' ')?.[0] || 'user'}`,
    },
  ] as ControlProps[];

const tabs = (resources) =>
  [
    {
      value: RESOURCE_TYPE_FLOW,
      label: `Flows (${resources?.flows?.length || 0})`,
      name: 'Flow',
    },
    {
      value: RESOURCE_TYPE_DATA_SET,
      label: `Nexsets (${resources?.data_sets?.length || 0})`,
      name: 'Nexset',
    },
    {
      value: RESOURCE_TYPE_SOURCE,
      label: `Sources (${resources?.data_sources?.length || 0})`,
      name: 'Source',
    },
    {
      value: RESOURCE_TYPE_SINK,
      label: `Destinations (${resources?.data_sinks?.length || 0})`,
      name: 'Destination',
    },
    {
      value: RESOURCE_TYPE_CREDENTIALS,
      label: `Credentials (${resources?.credentials?.length || 0})`,
      name: 'Credential',
    },
    {
      value: RESOURCE_TYPE_RECORD_TRANSFORM,
      label: `Reusable Transforms (${resources?.transforms?.length || 0})`,
      name: 'Transform',
    },
  ] as (PillTab & { name: string })[];

export const People360 = (props: People360Props) => {
  const containerRef = useRef();
  const [selected, setSelected] = useState(null);
  const [row, setRow] = useState(null);
  const [control, setControl] = useState(controls(selected)[0].value);
  const [tab, setTab] = useState(tabs({})[0].value);
  const user = useCurrentUser();
  const shared = useSharedResourcesInfo(selected?.id || user.id);
  const dispatch = useDispatch();

  const org = useCurrentOrg();

  useEffect(() => {
    dispatch(fetchOrgMembers(org.id));
    dispatch(loadMyTeams());
  }, [org.id]);

  const onControlChange = (_, value) => {
    setControl(value);
  };

  const onTabChange = (selection) => {
    setTab(selection);
  };

  const onChangeSelection = (e, selection) => {
    setSelected(selection);
  };

  const onClickRow = useCallback((row) => {
    setRow(row);
  }, []);

  return (
    <>
      <BannerDescription title="People 360°" description={description} />
      <div className={classes.dividerContainer}></div>
      <Stack height="100%">
        <Stack direction="row" gap="16px" alignItems="flex-end" className={classes.inputContainer}>
          <AclEntitySelect
            multiple={false}
            label="Find People and Teams"
            placeholder="Search users or teams"
            onChangeSelection={onChangeSelection}
            removeGroups={['orgs']}
          />
          <Button variant="contained">Search</Button>
        </Stack>
        {!!selected && (
          <Stack ref={containerRef} className={classes.contentContainer}>
            <div className={classes.fieldsInfo}>
              <Stack direction="row" alignItems="center" gap="8px">
                <Typography variant="body1">{selected.full_name || selected.name}</Typography>
                <div className={classes.role}>
                  <span>{selected.access_role}</span>
                </div>
              </Stack>
              <Typography variant="caption">{selected.email}</Typography>
            </div>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <SegmentedControl
                controls={controls(selected)}
                defaultValue={control}
                value={control}
                onControlChange={onControlChange}
              />
              <PillTabs tabs={tabs(shared)} onSelectionChange={onTabChange} selected={tab} />
            </Stack>
            <Table
              parentScrollRef={containerRef}
              getRowClassName={(r) => {
                if (r.id && row?.id) {
                  return classes.rowSelected;
                }
              }}
              onRowClick={onClickRow}
              columns={columns(tab, selected, row, control)}
              rows={shared?.[tab] || []}
            />
          </Stack>
        )}
      </Stack>
    </>
  );
};

People360.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_PEOPLE_360}`,
  domain: 'collaborate',
  header: {
    title: 'People 360º',
    slugTitle: 'People 360º',
  },
  screenName: 'People360',
};
